<template>
  <v-container v-if="!accessDecided" fluid class="pa-4">
    <v-skeleton-loader type="card, list-item-two-line@3" />
  </v-container>
  <v-container v-else-if="accessAllowed" fluid class="pa-0">
    <v-tabs v-model="tab" centered icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1" class="text-none font-weight-bold">
        Approve Leave
        <v-badge color="blue" :content="leavePendingCount" :value="leavePendingCount > 0" overlap>
          <v-icon>mdi-calendar-check</v-icon>
        </v-badge>
      </v-tab>
      <v-tab href="#tab-2" class="text-none font-weight-bold">
        Approve Overtime
        <v-badge color="blue" :content="overtimePendingCount" :value="overtimePendingCount > 0" overlap>
          <v-icon>mdi-timer-outline</v-icon>
        </v-badge>
      </v-tab>
      <v-tab href="#tab-3" class="text-none font-weight-bold">
        Approve WFH
        <v-badge color="blue" :content="wfhPendingCount" :value="wfhPendingCount > 0" overlap>
          <v-icon>mdi-home-account</v-icon>
        </v-badge>
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <LeaveApproval mode="leave" />
        </v-card>
      </v-tab-item>

      <v-tab-item value="tab-2">
        <v-card flat>
          <OverTimeApproval />
        </v-card>
      </v-tab-item>

      <v-tab-item value="tab-3">
        <v-card flat>
          <LeaveApproval mode="wfh" />
        </v-card>
      </v-tab-item>

    </v-tabs-items>
  </v-container>
</template>

<script>
/**components */
import LeaveApproval from "./LeaveApproval.vue";
import OverTimeApproval from "./OvertimeAdminApproval.vue";
import { canAccessLeaveApproval } from "../leave-management/leaveUtils";

/**vuex */
import { mapGetters } from "vuex";

export default {
  components: {
    LeaveApproval,
    OverTimeApproval,
  },
  inject: ["refreshApprovalCounts", "users", "roleManagers"],
  data() {
    return {
      tab: null,
      leavePendingCount: 0,
      overtimePendingCount: 0,
      wfhPendingCount: 0,
      countsLoaded: false,
    };
  },
  async mounted() {
    await Promise.all([this.loadLeavePendingCount(), this.loadOvertimePendingCount()]);
    this.countsLoaded = true;
    // Re-syncs the settings-menu gear badge, which is only fetched once at
    // app mount otherwise and can go stale relative to this hub's own counts.
    if (typeof this.refreshApprovalCounts === "function") {
      this.refreshApprovalCounts();
    }
  },
  computed: {
    ...mapGetters({
      user: "auth/get_user",
    }),
    // Only Super Admin, company managers, or someone with requests still waiting
    // on them may use this page. Decided once the users list and counts are in.
    accessDecided() {
      const list = this.users();
      return this.countsLoaded && Array.isArray(list) && list.length > 0;
    },
    accessAllowed() {
      const pending = this.leavePendingCount + this.overtimePendingCount + this.wfhPendingCount > 0;
      return canAccessLeaveApproval(this.user, this.users(), pending, this.teamManagerRows);
    },
    teamManagerRows() {
      return this.roleManagers ? this.roleManagers() : [];
    },
  },
  watch: {
    accessDecided: "enforceAccess",
    accessAllowed: "enforceAccess",
  },
  methods: {
    enforceAccess() {
      if (this.accessDecided && !this.accessAllowed) {
        this.$toast.error("Leave approval is only available to managers");
        this.$router.replace("/dashboard");
      }
    },
    async loadLeavePendingCount() {
      try {
        const { data } = await this.$axios.get("/api/leave/approval");
        const isWfh = (item) =>
          ((item.leave_type && item.leave_type.name) || "").trim().toLowerCase() === "work from home";
        this.wfhPendingCount = data.filter(isWfh).length;
        this.leavePendingCount = data.filter((item) => !isWfh(item)).length;
      } catch (error) {
        console.log("error on fetching leave approval count - component level", error);
      }
    },
    async loadOvertimePendingCount() {
      try {
        const [{ data: overview }, { data: statuses }] = await Promise.all([
          this.$axios.get("api/overtime/overview"),
          this.$axios.get("api/overtime/status"),
        ]);
        const pendingStatus = statuses.find((s) => (s.name || "").toLowerCase().includes("pending"));
        const pendingStatusId = pendingStatus ? pendingStatus.id : 1;
        this.overtimePendingCount = overview.filter(
          (item) => item.approver_id === this.user.id && item.overtime_status?.id === pendingStatusId
        ).length;
      } catch (error) {
        console.log("error on fetching overtime approval count - component level", error);
      }
    },
  },
};
</script>
